import { encodeFilters, FilterValueType } from 'app/filter.utils'
import type { AttributionRowType, AttributionSummaryType } from 'app/types/attribution'

// ============================================
// STATE TYPES
// ============================================

export type FlowStatus = `idle` | `filtering`

export type FlowStats = {
  leads: number
  appointments: number
  patients: number
  production: number
}

export type FlowState = {
  status: FlowStatus

  // Selections (any combination is valid)
  selectedSource: string | null
  selectedMedium: string | null
  selectedCampaign: string | null

  // Filtered data for each column (null = use initial data)
  filteredSourceRows: AttributionRowType[] | null
  filteredMediumRows: AttributionRowType[] | null
  filteredCampaignRows: AttributionRowType[] | null

  // Filtered stats (when selection is active)
  filteredStats: FlowStats | null

  // Loading state
  isLoading: boolean
}

// ============================================
// ACTION TYPES
// ============================================

export type FlowAction =
  | { type: `SELECT_SOURCE`; payload: string }
  | { type: `SELECT_MEDIUM`; payload: string }
  | { type: `SELECT_CAMPAIGN`; payload: string }
  | { type: `DESELECT_SOURCE` }
  | { type: `DESELECT_MEDIUM` }
  | { type: `DESELECT_CAMPAIGN` }
  | { type: `CLEAR_FILTERS` }
  | { type: `SET_LOADING`; payload: boolean }
  | {
      type: `SET_FILTERED_DATA`
      payload: {
        sourceRows?: AttributionRowType[]
        mediumRows?: AttributionRowType[]
        campaignRows?: AttributionRowType[]
        summary?: AttributionSummaryType | FlatSummary
        channelRows?: AttributionRowType[]
      }
    }

// ============================================
// INITIAL STATE
// ============================================

export const initialFlowState: FlowState = {
  status: `idle`,
  selectedSource: null,
  selectedMedium: null,
  selectedCampaign: null,
  filteredSourceRows: null,
  filteredMediumRows: null,
  filteredCampaignRows: null,
  filteredStats: null,
  isLoading: false,
}

// ============================================
// HELPERS
// ============================================

// Marketing metrics summary from the GraphQL filtered query response — flat
// numeric fields (`{ leads: number }`), unlike the initial-load
// `AttributionSummaryType` which nests a `count` (`{ leads: { count: number } }`).
export type FlatSummary = {
  leads?: number
  appointments?: number
  patients?: number
}

function getCount(field: { count?: number } | number | undefined): number {
  if (typeof field === `number`) return field
  return field?.count ?? 0
}

export function calculateStats(
  summary: AttributionSummaryType | FlatSummary | undefined,
  channelRows: AttributionRowType[] | undefined,
): FlowStats {
  const production = channelRows?.reduce((sum, row) => sum + (row.production ?? 0), 0) ?? 0

  return {
    leads: getCount(summary?.leads),
    appointments: getCount(summary?.appointments),
    patients: getCount(summary?.patients),
    production,
  }
}

export function hasAnySelection(state: FlowState): boolean {
  return state.selectedSource !== null || state.selectedMedium !== null || state.selectedCampaign !== null
}

// ============================================
// REDUCER
// ============================================

export function flowReducer(state: FlowState, action: FlowAction): FlowState {
  switch (action.type) {
    case `SELECT_SOURCE`: {
      return {
        ...initialFlowState,
        status: `filtering`,
        selectedSource: action.payload,
        filteredSourceRows: state.filteredSourceRows,
        filteredStats: state.filteredStats,
        isLoading: true,
      }
    }

    case `SELECT_MEDIUM`: {
      return {
        ...initialFlowState,
        status: `filtering`,
        selectedSource: state.selectedSource,
        selectedMedium: action.payload,
        filteredSourceRows: state.filteredSourceRows,
        filteredMediumRows: state.filteredMediumRows,
        filteredStats: state.filteredStats,
        isLoading: true,
      }
    }

    case `SELECT_CAMPAIGN`: {
      return {
        ...initialFlowState,
        status: `filtering`,
        selectedSource: state.selectedSource,
        selectedMedium: state.selectedMedium,
        selectedCampaign: action.payload,
        filteredSourceRows: state.filteredSourceRows,
        filteredMediumRows: state.filteredMediumRows,
        filteredCampaignRows: state.filteredCampaignRows,
        filteredStats: state.filteredStats,
        isLoading: true,
      }
    }

    case `DESELECT_SOURCE`: {
      if (state.selectedMedium || state.selectedCampaign) {
        return {
          ...state,
          selectedSource: null,
          filteredSourceRows: null,
          isLoading: true,
        }
      }
      return { ...initialFlowState }
    }

    case `DESELECT_MEDIUM`: {
      if (state.selectedSource || state.selectedCampaign) {
        return {
          ...state,
          selectedMedium: null,
          filteredMediumRows: null,
          isLoading: true,
        }
      }
      return { ...initialFlowState }
    }

    case `DESELECT_CAMPAIGN`: {
      if (state.selectedSource || state.selectedMedium) {
        return {
          ...state,
          selectedCampaign: null,
          filteredCampaignRows: null,
          isLoading: true,
        }
      }
      return { ...initialFlowState }
    }

    case `CLEAR_FILTERS`: {
      return { ...initialFlowState }
    }

    case `SET_LOADING`: {
      return {
        ...state,
        isLoading: action.payload,
      }
    }

    case `SET_FILTERED_DATA`: {
      const { sourceRows, mediumRows, campaignRows, summary, channelRows } = action.payload
      const filteredStats = summary && channelRows ? calculateStats(summary, channelRows) : state.filteredStats

      // Always accept incoming data from the current fetch. However, when a
      // column has a selection and the API returns an empty array (the server
      // cannot resolve the over-constrained filter), preserve the existing
      // rows so the selected item stays visible.
      const keepIfSelectedEmpty = (
        incoming: AttributionRowType[] | undefined,
        existing: AttributionRowType[] | null,
        hasSelection: boolean,
      ): AttributionRowType[] | null => {
        if (incoming === undefined) return existing ?? null
        if (incoming.length === 0) {
          // Selected column with existing data: preserve it (shows selected item)
          if (hasSelection && existing && existing.length > 0) return existing
          // Otherwise: return null so the selector falls through to initialRows
          return null
        }
        return incoming
      }

      // Preserve stats when the API returns all-zero but selections exist
      const hasSelections = !!state.selectedSource || !!state.selectedMedium || !!state.selectedCampaign
      const apiReturnedEmpty = !sourceRows?.length && !mediumRows?.length && !campaignRows?.length
      const preserveStats = hasSelections && apiReturnedEmpty && state.filteredStats

      return {
        ...state,
        status: hasAnySelection(state) ? `filtering` : `idle`,
        filteredSourceRows: keepIfSelectedEmpty(sourceRows, state.filteredSourceRows, !!state.selectedSource),
        filteredMediumRows: keepIfSelectedEmpty(mediumRows, state.filteredMediumRows, !!state.selectedMedium),
        filteredCampaignRows: keepIfSelectedEmpty(campaignRows, state.filteredCampaignRows, !!state.selectedCampaign),
        filteredStats: preserveStats ? state.filteredStats : filteredStats,
        isLoading: false,
      }
    }

    default:
      return state
  }
}

// ============================================
// SELECTORS
// ============================================

export function selectSourceRows(
  state: FlowState,
  initialSourceRows: AttributionRowType[] | undefined,
): AttributionRowType[] | undefined {
  if (state.status === `idle`) return initialSourceRows
  return state.filteredSourceRows ?? initialSourceRows
}

export function selectMediumRows(
  state: FlowState,
  initialMediumRows: AttributionRowType[] | undefined,
): AttributionRowType[] | undefined {
  if (state.status === `idle`) return initialMediumRows
  return state.filteredMediumRows ?? initialMediumRows
}

export function selectCampaignRows(
  state: FlowState,
  initialCampaignRows: AttributionRowType[] | undefined,
): AttributionRowType[] | undefined {
  if (state.status === `idle`) return initialCampaignRows
  return state.filteredCampaignRows ?? initialCampaignRows
}

export function selectStats(
  state: FlowState,
  initialSummary: AttributionSummaryType | undefined,
  initialChannelRows: AttributionRowType[] | undefined,
): FlowStats {
  if (state.status === `idle` || !state.filteredStats) {
    return calculateStats(initialSummary, initialChannelRows)
  }
  return state.filteredStats
}

export function selectHasFilters(state: FlowState): boolean {
  return state.selectedSource !== null || state.selectedMedium !== null || state.selectedCampaign !== null
}

export function selectIsFiltered(state: FlowState, column: `sources` | `mediums` | `campaigns`): boolean {
  if (state.status === `idle`) return false

  switch (column) {
    case `sources`:
      return state.filteredSourceRows !== null
    case `mediums`:
      return state.filteredMediumRows !== null
    case `campaigns`:
      return state.filteredCampaignRows !== null
  }
}

// ============================================
// FILTER ENCODING
// ============================================

export function buildFilterParam(
  source: string | null,
  medium: string | null,
  campaign: string | null,
): string | undefined {
  const filterObjects: Parameters<typeof encodeFilters>[0] = []

  // Use .keyword suffix for exact matching (same as modal queries)
  if (source) {
    filterObjects.push({
      attribute: `utmSource.keyword`,
      displayName: `Source`,
      type: FilterValueType.stringArray,
      operator: `in`,
      value: [source],
    })
  }

  if (medium) {
    filterObjects.push({
      attribute: `utmMedium.keyword`,
      displayName: `Medium`,
      type: FilterValueType.stringArray,
      operator: `in`,
      value: [medium],
    })
  }

  if (campaign) {
    filterObjects.push({
      attribute: `utmCampaign.keyword`,
      displayName: `Campaign`,
      type: FilterValueType.stringArray,
      operator: `in`,
      value: [campaign],
    })
  }

  return filterObjects.length ? encodeFilters(filterObjects) : undefined
}
